import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Alert
} from '@mui/material';
import { COLORS, glassCardSx, accentBarSx, alertInfoSx } from './clientPortalTheme';

const SECTOR_FLOW = [
  { key: 'PLANNED', label: 'Planned' },
  { key: 'ASSIGNED', label: 'Assigned' },
  { key: 'CREW_ACCEPTED', label: 'Crew Accepted' },
  { key: 'READY_FOR_BA', label: 'Ready for BA' },
  { key: 'BA_PASSED', label: 'BA Passed' },
  { key: 'READY_FOR_ATC', label: 'Ready for ATC' },
  { key: 'ATC_PASSED', label: 'ATC Passed' },
  { key: 'CLEARED', label: 'Cleared' },
  { key: 'COMPLETED', label: 'Completed' }
];

const FAILURE_STATUSES = new Set(['BA_FAILED', 'ATC_FAILED', 'CREW_REJECTED', 'CANCELLED']);

function getActiveStepIndex(status) {
  if (!status) return 0;
  if (status === 'SECTOR_COMPLETED') return SECTOR_FLOW.length - 1;
  const idx = SECTOR_FLOW.findIndex((s) => s.key === status);
  return idx >= 0 ? idx : 0;
}

const formatDateTime = (date, time) => {
  if (!date) return '—';
  const d = new Date(date).toLocaleDateString();
  return time ? `${d} ${time}` : d;
};

const ClientCurrentSectorCard = ({ currentSector, activeCharter }) => {
  if (!currentSector) {
    return (
      <Alert severity="info" sx={{ mb: 2, ...alertInfoSx }}>
        No active sector — aircraft is not currently on a charter leg.
      </Alert>
    );
  }

  const activeStep = getActiveStepIndex(currentSector.status);
  const isFailure = FAILURE_STATUSES.has(currentSector.status);

  return (
    <Paper
      elevation={0}
      sx={{
        ...glassCardSx,
        p: 3,
        mb: 3
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box sx={accentBarSx}>
          <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.text }}>Current Sector</Typography>
        </Box>
        <Chip
          label={currentSector.status?.replace(/_/g, ' ')}
          size="small"
          sx={{
            fontWeight: 700,
            borderRadius: 999,
            bgcolor: isFailure ? COLORS.red : COLORS.indigo,
            color: COLORS.white
          }}
        />
      </Box>

      <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ color: COLORS.text }}>
        Sector {currentSector.seqNo}: {currentSector.departure?.airport} → {currentSector.arrival?.airport}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Departure</Typography>
          <Typography>
            {formatDateTime(currentSector.departure?.date, currentSector.departure?.time)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Estimated Arrival</Typography>
          <Typography>
            {formatDateTime(currentSector.arrival?.estimatedDate, currentSector.arrival?.estimatedTime)}
          </Typography>
        </Grid>
        {currentSector.wakeTurbulence && (
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Wake Turbulence</Typography>
            <Typography>{currentSector.wakeTurbulence}</Typography>
          </Grid>
        )}
        {currentSector.flightRules && (
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Flight Rules</Typography>
            <Typography>{currentSector.flightRules}</Typography>
          </Grid>
        )}
        {currentSector.specialRequirements && (
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Special Requirements</Typography>
            <Typography>{currentSector.specialRequirements}</Typography>
          </Grid>
        )}
      </Grid>

      <Box sx={{ overflowX: 'auto', pb: 1 }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            minWidth: 700,
            '& .MuiStepLabel-label.Mui-active': { color: COLORS.indigoLight, fontWeight: 700 },
            '& .MuiStepLabel-label.Mui-completed': { color: COLORS.textMuted },
            '& .MuiStepIcon-root.Mui-active': { color: COLORS.indigo },
            '& .MuiStepIcon-root.Mui-completed': { color: COLORS.greyMuted }
          }}
        >
          {SECTOR_FLOW.map((step) => (
            <Step key={step.key} completed={activeStep > SECTOR_FLOW.indexOf(step)}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {currentSector.assignedPilots?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Crew</Typography>
          <Typography>
            {currentSector.assignedPilots
              .map((p) => `${p.firstName || ''} ${p.lastName || ''}`.trim())
              .filter(Boolean)
              .join(', ')}
          </Typography>
        </Box>
      )}

      {currentSector.passengers?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Passengers ({currentSector.passengers.length})</Typography>
          <Typography variant="body2">
            {currentSector.passengers.map((p) => p.name || 'Unnamed').join(', ')}
          </Typography>
        </Box>
      )}

      {activeCharter?.specialRequirements && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Charter Requirements</Typography>
          <Typography variant="body2">{activeCharter.specialRequirements}</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ClientCurrentSectorCard;
